import { CommonModule } from "@angular/common";
import { Component, EventEmitter, HostListener, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap } from "rxjs";
import { Helper } from "src/app/core/helpers/helper";
import { LocationService } from "src/app/shared/services/location.service";
import { GoogleMapsAPIService } from "../../services/google-maps-api.service";

declare const google: any;

@Component({
  selector: "app-address-modal",
  templateUrl: "./address-modal.component.html",
  styleUrl: "./address-modal.component.scss",
  imports: [CommonModule, TranslateModule, FormsModule, ReactiveFormsModule],
  standalone: true
})
export class AddressModalComponent implements OnInit {
  selectedAddress: any;
  selectedAddressType: any;
  countryDetail: any;
  addressModalRef!: NgbModalRef;
  isEditAddress: boolean = false;
  addressForm!: FormGroup;
  addressMap: any = null;
  placeAddress: string = "";
  selectedSearchAddress!: string;

  addressLocationMarker: any;
  predictions: any = [];
  selectedPlace: any;
  selectedSearchIndex: number = -1;
  addressLocation: [number, number] = [0, 0];

  modalConfig: NgbModalOptions = { size: "lg", centered: true, backdrop: "static", keyboard: false, scrollable: true };

  private inputSubject: Subject<string> = new Subject<string>();
  private inputSubscription!: Subscription;

  @Output() addressUpdated = new EventEmitter();

  @ViewChild("addressModal", { static: true }) addressModal!: TemplateRef<any>;

  constructor(
    public helper: Helper,
    public modalService: NgbModal,
    private fb: FormBuilder,
    private googleMapsApiService: GoogleMapsAPIService,
    private locationService: LocationService
  ) {}

  /**
   * Initializes the component and sets up the address form.
   */
  ngOnInit(): void {
    this.initAddressForm();
  }

  /**
   * Handles keyboard events for navigating address predictions.
   *
   * @param event The keyboard event.
   */
  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const mapType = this.helper.adminSettings?.mapType;

    if (mapType === this.helper.MapType.GOOGLE && this.predictions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedSearchIndex = (this.selectedSearchIndex + 1) % this.predictions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedSearchIndex = (this.selectedSearchIndex - 1 + this.predictions.length) % this.predictions.length;
      } else if (event.key === "Enter" && this.selectedSearchIndex >= 0 && this.selectedSearchIndex < this.predictions.length) {
        this.onPredictionSelect(this.predictions[this.selectedSearchIndex]);
        this.selectedSearchIndex = -1;
      }
    }
  }

  /**
   * Opens the address modal and initializes the map and form based on the provided parameters.
   *
   * @param isEditAddress Indicates if the address is being edited.
   * @param addressType The type of address (home, work, etc.).
   * @param address The address details.
   */
  openModal(address?: any): void {
    if (this.helper.adminSettings?.mapType === this.helper.MapType.GOOGLE) {
      this.addressAutoComplete();
    }

    this.addressModalRef = this.modalService.open(this.addressModal, this.modalConfig);

    this.addressModalRef.result.then(
      () => this.closeModal(),
      () => this.closeModal()
    );

    setTimeout(() => {
      this.initAddressMap();
    }, 100);

    if (!this.selectedAddress) {
      this.setCurrentLocation(address);
    }
  }

  /**
   * Sets the current location.
   */
  setCurrentLocation(address?: any): void {
    const mapType = this.helper.adminSettings?.mapType;
    if (address && address.address) {
      setTimeout(() => {
        if (mapType === this.helper.MapType.GOOGLE) {
          this.setAddressLocationMarker(address.latitude, address.longitude, 16);
        }
      }, 200);
      this.addressForm.patchValue({
        placeAddress: address.address,
        address: address
      });
    } else {
      this.locationService.setCurrentLocationAsync().then((currentAddress: any) => {
        setTimeout(() => {
          if (mapType === this.helper.MapType.GOOGLE) {
            this.setAddressLocationMarker(currentAddress.latitude, currentAddress.longitude, 16);
          }
        }, 200);
        this.addressForm.patchValue({
          placeAddress: currentAddress.address,
          address: currentAddress
        });
      });
    }
  }

  /**
   * Initializes the address form with validation rules.
   */
  initAddressForm(): void {
    this.addressForm = this.fb.group({
      placeAddress: [null, Validators.required],
      address: [null, Validators.required]
    });
  }

  /**
   * Patches the address form with the selected address details.
   *
   * @param selectedAddress The selected address details.
   */
  patchAddressForm(selectedAddress: any): void {
    const { address } = selectedAddress;

    // Patch the basic fields
    this.addressForm.patchValue({
      placeAddress: address,
      address: this.selectedAddress
    });
  }

  /**
   * Initializes the address map if it is not already initialized.
   */
  initAddressMap(): void {
    if (this.addressMap) return; // Early return if maps are already initialized

    const mapElement: HTMLElement | null = document.getElementById("address-map");
    if (!mapElement) return;
    if (this.helper.adminSettings?.mapType === this.helper.MapType.GOOGLE) {
      this.addressMap = new google.maps.Map(mapElement, {
        zoom: 2,
        streetViewControl: false,
        scrollwheel: true,
        center: { lat: 0, lng: 0 }
      });
    }
  }

  /**
   * Checks if the given index is the currently selected search index.
   *
   * @param index The index to check.
   * @returns True if the index is selected, false otherwise.
   */
  isSelected(index: number): boolean {
    return this.selectedSearchIndex === index;
  }

  /**
   * Sets up address autocomplete functionality using Google Maps API.
   */
  addressAutoComplete(): void {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
    this.inputSubscription = this.inputSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(async (input) => {
          this.predictions = [];
          return await this.googleMapsApiService.getPlacePredictions(input, {});
        })
      )
      .subscribe((predictions) => {
        this.predictions = predictions;
      });
  }

  /**
   * Handles input change events and triggers address predictions.
   *
   * @param event The input change event.
   */
  onInputChange(event: Event): void {
    const mapType = this.helper.adminSettings?.mapType;

    if (mapType === this.helper.MapType.GOOGLE) {
      const inputElement = event.target as HTMLInputElement;
      const inputValue = inputElement.value.trim();

      this.inputSubject.next(inputValue);
    }
  }

  /**
   * Handles the selection of an address prediction.
   *
   * @param prediction The selected address prediction.
   */
  async onPredictionSelect(prediction: any): Promise<void> {
    this.selectedSearchAddress = prediction.description;
    this.predictions = [];

    const place = await this.googleMapsApiService.fetchFields(prediction);
    this.selectedPlace = place;
    this.selectedPlace.placeId = prediction.place_id;
    this.onPlaceSelected(this.selectedPlace);
  }

  /**
   * Handles the selection of a place and updates the form and map accordingly.
   *
   * @param place The selected place details.
   */
  onPlaceSelected(place: any): void {
    const address = this.helper.parseAddressComponents(place);
    address.address = this.selectedSearchAddress;

    this.placeAddress = JSON.parse(JSON.stringify(address?.address));
    this.addressForm.patchValue({
      address: address,
      placeAddress: this.placeAddress
    });

    this.setAddressLocationMarker(address.latitude, address.longitude, 16);
  }

  /**
   * Sets the address location marker on the map and updates the form with the address details.
   *
   * @param lat The latitude of the location.
   * @param lng The longitude of the location.
   * @param zoom The zoom level for the map.
   */
  setAddressLocationMarker(lat: number, lng: number, zoom: number = 12): void {
    if (this.addressLocationMarker) {
      this.addressLocationMarker.setMap(null);
      this.addressLocationMarker = null;
    }

    const bounds = new google.maps.LatLngBounds();
    const addressLatLng = new google.maps.LatLng({ lat, lng });

    this.addressLocationMarker = new google.maps.Marker({
      position: addressLatLng,
      map: this.addressMap,
      draggable: true,
      icon: this.helper.DefaultImages.LocationPinImage
    });

    bounds.extend(this.addressLocationMarker.position);
    this.addressMap.fitBounds(bounds);

    // Center the map and set the zoom level
    this.addressMap.setCenter({ lat, lng });
    this.addressMap.setZoom(zoom);

    // Update the addressLocation property
    this.addressLocation = [lat, lng];

    google.maps.event.addListener(this.addressLocationMarker, "dragend", (marker: any): void => {
      const latitude = marker.latLng.lat();
      const longitude = marker.latLng.lng();

      const geocoder = new google.maps.Geocoder();
      const location = new google.maps.LatLng(latitude, longitude);
      const request = { latLng: location };

      this.addressLocationMarker.setPosition(location);
      this.addressMap.setCenter(location);

      geocoder.geocode(request, (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const place = results[0];
          const address = this.helper.parseAddressComponents(place);

          this.placeAddress = JSON.parse(JSON.stringify(address?.address));
          this.addressForm.patchValue({
            address: address,
            placeAddress: this.placeAddress
          });
        }
      });
    });
  }

  /**
   * Clears the address marker from the map.
   */
  clearAddressMarker(): void {
    const mapType = this.helper.adminSettings?.mapType;

    if (mapType === this.helper.MapType.GOOGLE) {
      if (this.addressLocationMarker) {
        this.addressLocationMarker.setMap(null);
        this.addressLocationMarker = null;
      }
    } else if (mapType === this.helper.MapType.MAP_BOX && this.addressLocationMarker) {
      this.addressLocationMarker.remove();
    }
  }

  /**
   * Saves the address form data and updates the address list.
   */
  onSaveAddress(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }
    const address: any = this.addressForm.value.address;
    this.addressUpdated.emit(address);
    this.closeModal();
  }

  /**
   * Closes the currently open modal dialog.
   */
  closeModal(): void {
    this.addressModalRef?.dismiss(this.addressModal);
    this.addressForm.reset();
    this.isEditAddress = false;
    this.selectedAddress = null;
    this.clearAddressMarker();
    this.addressMap = null;
    this.predictions = [];
  }

  /**
   * Unsubscribes from the input subscription when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.inputSubscription?.unsubscribe();
  }

  /**
   * Gets the address input form control.
   *
   * @returns The address input form control.
   */
  get addressInput(): any {
    return this.addressForm.get("address");
  }
}
